// Everything the project view shows, read once and kept fresh: episodes → scenes → shots → takes, plus
// generations per episode (polled while something is generating), elements, canvases and costs.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Api } from "./api.ts";
import { boardMaps, type ShotHome } from "./board.ts";
import type { CanvasInfo } from "./canvas/types.ts";
import { hasActive, num, pool, shotsToRefresh } from "./lib.ts";
import {
  checkShotRow,
  generationsKey,
  isLooseKey,
  looseKey,
  looseStateOfError,
  shotIdOfKey,
  withLooseShot,
} from "./looseShot.ts";
import { createShotRefreshQueue } from "./projectRefresh.ts";
import type { Costs, Element, Episode, Generation, Project, Scene, Shot, TakesResponse } from "./types.ts";

export type { ShotHome } from "./board.ts";

export interface ProjectData {
  project: Project;
  episodes: Episode[];
  scenes: Map<string, Scene[]>;
  shots: Map<string, Shot[]>;
  shotHome: Map<string, ShotHome>;
  shotById: Map<string, Shot>;
  /** Shots opened on their own that sit in no scene (see looseShot.ts). Never part of `shotById`. */
  loose?: Map<string, Shot>;
  takes: Map<string, TakesResponse>;
  sceneTakes: Map<string, TakesResponse>;
  generations: Map<string, Generation[]>;
  costs: Map<string, Costs | null>;
  elements: Element[];
  canvases: CanvasInfo[];
}

export interface UseProject {
  data: ProjectData | null;
  loading: boolean;
  error: string | null;
  /** reloads everything; answers true when the new data is in place (false: it failed or a newer load took over) */
  reload: () => Promise<boolean>;
  refreshShots: (shotIds: string[]) => Promise<void>;
  refreshElements: () => Promise<void>;
  refreshCanvases: () => Promise<void>;
  /** Open a shot by id that is in no scene: reads it, its takes and its generations. Rejects when it is gone. */
  loadLooseShot: (shotId: string) => Promise<void>;
  /** Put a just-started generation in place so polling starts at once. */
  noteGeneration: (shotId: string, g: Generation) => void;
  /** Put a shot row the server just answered in place, wherever the shot sits. */
  noteShot: (row: Shot) => void;
}

export const POLL_MS = 2000;
const KEPT_PROJECTS = 5;
/** The last productions opened, per signed-in client: going back shows them at once while they are read again. */
const kept = new WeakMap<Api, Map<string, ProjectData>>();

export const keptProject = (api: Api, key: string): ProjectData | null => kept.get(api)?.get(key) ?? null;

/** Signed out: the next person on this tab sees nothing of the last one's productions. */
export const forgetKeptProjects = (api: Api): void => {
  kept.delete(api);
};

export function keepProject(api: Api, key: string, d: ProjectData): void {
  const m = kept.get(api) ?? new Map<string, ProjectData>();
  kept.set(api, m);
  m.delete(key);
  m.set(key, d);
  if (m.size > KEPT_PROJECTS) m.delete(m.keys().next().value as string);
}
const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** The data with a shot's new row in every scene it is placed in. Pure. */
export function withShotRow(d: ProjectData, row: Shot): ProjectData {
  const merge = (s: Shot): Shot => (s.id === row.id ? { ...s, ...row } : s);
  const shots = new Map<string, Shot[]>();
  for (const [sceneId, list] of d.shots)
    shots.set(sceneId, list.some((s) => s.id === row.id) ? list.map(merge) : list);
  const old = d.shotById.get(row.id);
  const shotById = old ? new Map(d.shotById).set(row.id, merge(old)) : d.shotById;
  return { ...d, shots, shotById };
}

export function projectCostUsd(d: ProjectData): number {
  let t = 0;
  for (const c of d.costs.values()) t += num(c?.total_usd) ?? 0;
  return t;
}

export function useProject(api: Api, ws: string, projectId: string): UseProject {
  const keepKey = `${ws}:${projectId}`;
  const [data, setData] = useState<ProjectData | null>(() => keptProject(api, keepKey));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<ProjectData | null>(null);
  const gen = useRef(0);
  const dirtyShots = useRef(createShotRefreshQueue());
  const pollInFlight = useRef(false);
  /** A read has come in for this production since it opened: until then, what shows is only what was kept. */
  const readOnce = useRef(false);
  /** Changes a running read would overwrite: when one happens, the read starts over. */
  const changes = useRef(0);

  const commit = useCallback(
    (d: ProjectData) => {
      ref.current = d;
      keepProject(api, keepKey, d);
      setData(d);
    },
    [api, keepKey],
  );

  const takesOf = useCallback(
    (ids: string[]) =>
      pool(
        6,
        ids.map((id) => () => api.listShotTakes(ws, id).then((r) => [id, r] as const)),
      ),
    [api, ws],
  );

  const sceneTakesOf = useCallback(
    (ids: string[]) =>
      pool(
        6,
        ids.map((id) => () => api.listSceneTakes(ws, id).then((r) => [id, r] as const)),
      ),
    [api, ws],
  );

  const load = useCallback(async (): Promise<boolean> => {
    const g = ++gen.current;
    const changesAtStart = changes.current;
    setLoading(true);
    setError(null);
    try {
      // One read for the board (episodes → scenes → shots → takes). Generations and costs are per episode:
      // they start as soon as the (small) episode list is in, next to the board read.
      type PerEpisode = [(readonly [string, Generation[]])[], (readonly [string, Costs | null])[]];
      const perEpisode = api.listEpisodes(ws, projectId).then(
        (eps): Promise<PerEpisode> =>
          Promise.all([
            pool(
              4,
              eps.map((e) => () => api.listGenerations(ws, e.id).then((l) => [e.id, l] as const)),
            ),
            pool(
              4,
              eps.map(
                (e) => () =>
                  api
                    .episodeCosts(ws, e.id)
                    .catch(() => null)
                    .then((c) => [e.id, c] as const),
              ),
            ),
          ]),
      );
      perEpisode.catch(() => {}); // awaited below; keeps an early failure from going unhandled
      const [projects, board, elements, canvases] = await Promise.all([
        api.listProjects(ws),
        api.getProjectBoard(ws, projectId),
        api.listElements(ws, { inProject: projectId }),
        api.listCanvases(ws, { projectId }).catch(() => [] as CanvasInfo[]),
      ]);
      const project = projects.find((p) => p.id === projectId);
      if (!project) throw new Error("This production does not exist in this workspace, or it was archived.");
      const { episodes, scenes, shots, shotHome, shotById, takes: boardTakes, sceneTakes } = boardMaps(board);
      // Shots opened on their own stay open across a reload, unless they were placed meanwhile.
      const carriedRows = await pool(
        6,
        [...(ref.current?.loose?.keys() ?? [])]
          .filter((id) => !shotById.has(id))
          .map((id) => async () => {
            try {
              const row = await api.getShot(ws, id);
              return checkShotRow(row, projectId).s === "ready" ? row : null;
            } catch (e) {
              if (looseStateOfError(e).s === "missing") return null;
              throw e;
            }
          }),
      );
      const carried = carriedRows.flatMap((row) => (row ? [[row.id, row] as const] : []));
      const [carriedTakes, [gens, costs], carriedGens] = await Promise.all([
        takesOf(carried.map(([id]) => id)),
        perEpisode,
        pool(
          4,
          carried.map(
            ([id]) =>
              () =>
                api.listShotGenerations(ws, id).then((l) => [looseKey(id), l] as const),
          ),
        ),
      ]);
      if (g !== gen.current) return false;
      if (changes.current !== changesAtStart) return load();
      readOnce.current = true;
      commit({
        loose: new Map(carried),
        project,
        episodes,
        scenes,
        shots,
        shotHome,
        shotById,
        takes: new Map([...boardTakes, ...carriedTakes]),
        sceneTakes,
        generations: new Map([...gens, ...carriedGens]),
        costs: new Map(costs),
        elements: elements.filter((e) => !e.archived_at),
        canvases: canvases.filter((c) => !c.archivedAt),
      });
      return true;
    } catch (e) {
      if (g === gen.current) {
        setError(msg(e));
        // What was kept could not be confirmed: it may be gone, or out of this person's reach.
        if (!readOnce.current) {
          kept.get(api)?.delete(keepKey);
          ref.current = null;
          setData(null);
        }
      }
      return false;
    } finally {
      if (g === gen.current) setLoading(false);
    }
  }, [api, ws, projectId, keepKey, commit, takesOf]);

  /** The generations behind a key: an episode's, or those of one unplaced shot. */
  const generationsFor = useCallback(
    (key: string) =>
      isLooseKey(key) ? api.listShotGenerations(ws, shotIdOfKey(key)) : api.listGenerations(ws, key),
    [api, ws],
  );

  const refreshShots = useCallback(
    async (shotIds: string[]) => {
      const cur = ref.current;
      if (!cur) return;
      const epoch = gen.current;
      const queue = dirtyShots.current;
      queue.mark(shotIds);
      const keys = [...new Set(shotIds.map((id) => generationsKey(cur, id)))];
      const eps = keys.filter((k) => !isLooseKey(k));
      try {
        await queue.refresh(shotIds, async () => {
          const [takes, gens, costs, sceneTakes] = await Promise.all([
            takesOf(shotIds),
            pool(
              4,
              keys.map((k) => () => generationsFor(k).then((l) => [k, l] as const)),
            ),
            pool(
              4,
              eps.map(
                (e) => () =>
                  api
                    .episodeCosts(ws, e)
                    .catch(() => cur.costs.get(e) ?? null)
                    .then((c) => [e, c] as const),
              ),
            ),
            sceneTakesOf([...new Set(shotIds.flatMap((id) => cur.shotHome.get(id)?.sceneId ?? []))]),
          ]);
          const now = ref.current;
          if (!now || epoch !== gen.current) return;
          const t = new Map(now.takes);
          for (const [id, r] of takes) t.set(id, r);
          const g = new Map(now.generations);
          for (const [id, l] of gens) g.set(id, l);
          const c = new Map(now.costs);
          for (const [id, x] of costs) c.set(id, x);
          const st = new Map(now.sceneTakes);
          for (const [id, r] of sceneTakes) st.set(id, r);
          commit({ ...now, takes: t, sceneTakes: st, generations: g, costs: c });
          setError(null);
        });
      } catch (e) {
        if (epoch === gen.current) setError(msg(e));
      }
    },
    [api, ws, takesOf, sceneTakesOf, commit, generationsFor],
  );

  const loadLooseShot = useCallback(
    async (shotId: string) => {
      // A reload of the project while this runs replaces the data under it: read again, a few times at most.
      for (let tries = 0; tries < 3; tries++) {
        const epoch = gen.current;
        const row = await api.getShot(ws, shotId);
        const ok = checkShotRow(row, projectId);
        if (ok.s !== "ready") {
          throw Object.assign(new Error("This shot does not exist in this production."), { status: 404 });
        }
        const [takes, gens] = await Promise.all([
          api.listShotTakes(ws, shotId),
          api.listShotGenerations(ws, shotId),
        ]);
        const now = ref.current;
        if (now && epoch === gen.current) {
          changes.current++;
          commit(withLooseShot(now, row, takes, gens));
          return;
        }
      }
      throw new Error("The production kept changing while the shot was opened. Try again.");
    },
    [api, ws, projectId, commit],
  );

  const refreshElements = useCallback(async () => {
    const els = await api.listElements(ws, { inProject: projectId });
    if (ref.current) commit({ ...ref.current, elements: els.filter((e) => !e.archived_at) });
  }, [api, ws, projectId, commit]);

  const refreshCanvases = useCallback(async () => {
    const list = await api.listCanvases(ws, { projectId });
    if (ref.current) commit({ ...ref.current, canvases: list.filter((c) => !c.archivedAt) });
  }, [api, ws, projectId, commit]);

  const noteGeneration = useCallback(
    (shotId: string, g: Generation) => {
      const cur = ref.current;
      if (!cur || !(cur.shotHome.has(shotId) || cur.loose?.has(shotId))) return;
      const ep = generationsKey(cur, shotId);
      const m = new Map(cur.generations);
      m.set(ep, [g, ...(m.get(ep) ?? []).filter((x) => x.id !== g.id)]);
      changes.current++;
      commit({ ...cur, generations: m });
    },
    [commit],
  );

  const noteShot = useCallback(
    (row: Shot) => {
      if (!ref.current) return;
      changes.current++;
      commit(withShotRow(ref.current, row));
    },
    [commit],
  );

  /** Poll step: generations of episodes with work in flight; takes only for shots that changed. */
  const poll = useCallback(async () => {
    const cur = ref.current;
    if (!cur || pollInFlight.current) return;
    pollInFlight.current = true;
    const epoch = gen.current;
    const busy = [...cur.generations.entries()].filter(([, l]) => hasActive(l)).map(([e]) => e);
    try {
      const fresh = await Promise.all(busy.map((e) => generationsFor(e).then((l) => [e, l] as const)));
      const takeToShot = new Map<string, string>();
      for (const [shotId, r] of cur.takes) for (const t of r.items) takeToShot.set(t.id, shotId);
      const dirty = new Set<string>();
      for (const [e, l] of fresh) {
        const d = shotsToRefresh(cur.generations.get(e) ?? [], l, takeToShot);
        if (d === "all") {
          if (isLooseKey(e)) dirty.add(shotIdOfKey(e));
          else for (const [sid, h] of cur.shotHome) if (h.episodeId === e) dirty.add(sid);
        } else for (const s of d) dirty.add(s);
      }
      const now = ref.current;
      if (!now || epoch !== gen.current) return;
      dirtyShots.current.mark([...dirty]);
      // Nothing new: no commit, so a poll does not re-render every screen that reads the project.
      const changed = fresh.some(([e, l]) => JSON.stringify(now.generations.get(e)) !== JSON.stringify(l));
      if (changed) {
        const g = new Map(now.generations);
        for (const [e, l] of fresh) g.set(e, l);
        commit({ ...now, generations: g });
      }
      const pending = dirtyShots.current.ids();
      if (pending.length > 0) await refreshShots(pending);
    } catch (e) {
      if (epoch === gen.current) setError(msg(e));
    } finally {
      pollInFlight.current = false;
    }
  }, [commit, refreshShots, generationsFor]);

  useEffect(() => {
    const known = keptProject(api, keepKey);
    ref.current = known;
    readOnce.current = false;
    dirtyShots.current = createShotRefreshQueue();
    setData(known);
    void load();
    return () => {
      gen.current++;
    };
  }, [load, api, keepKey]);

  const polling =
    !!data && ([...data.generations.values()].some(hasActive) || dirtyShots.current.ids().length > 0);
  useEffect(() => {
    if (!polling) return;
    const t = setInterval(() => {
      if (!document.hidden) void poll();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [polling, poll]);

  return {
    data,
    loading,
    error,
    reload: load,
    refreshShots,
    refreshElements,
    refreshCanvases,
    loadLooseShot,
    noteGeneration,
    noteShot,
  };
}
